import { useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { CLASSIFICATIONS, RECOMMENDATIONS, type ScalingOutput, type ComparisonOutput } from "@shared/scaling.ts";

const TONE: Record<string, string> = { ok: "var(--green-600)", warn: "var(--amber-600)", bad: "var(--red-600)", neutral: "var(--gray-500)" };

export default function Scaling() {
  const { activeBrand } = useBrands();
  const [tab, setTab] = useState<"analyze" | "compare">("analyze");
  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">วางแผน Scale <span className="mh-muted mh-eng" style={{ fontSize: 15 }}>Scaling Advisor</span></div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b> — ตัดสินใจ Scale/Hold/Kill</> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className={"mh-btn mh-btn--sm " + (tab === "analyze" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("analyze")}>วิเคราะห์ & ตัดสินใจ</button>
          <button className={"mh-btn mh-btn--sm " + (tab === "compare" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("compare")}>เทียบช่วงเวลา</button>
        </div>
      </div>
      {tab === "analyze" ? <AnalyzeTab brandId={activeBrand?.id ?? null} /> : <CompareTab />}
    </div>
  );
}

const M_FIELDS = [
  { key: "daysRunning", label: "รันมากี่วัน" }, { key: "spend", label: "Spend ฿" }, { key: "lead", label: "Lead" },
  { key: "cpl", label: "CPL ฿" }, { key: "sale", label: "ยอดขาย" }, { key: "roas", label: "ROAS" },
  { key: "frequency", label: "Frequency" }, { key: "creativeAgeDays", label: "อายุ Creative (วัน)" },
];

function AnalyzeTab({ brandId }: { brandId: string | null }) {
  const [m, setM] = useState<Record<string, string>>({});
  const [out, setOut] = useState<ScalingOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setM((p) => ({ ...p, [k]: v }));

  async function analyze() {
    if (!brandId) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null);
    try {
      const num: Record<string, any> = {};
      for (const [k, v] of Object.entries(m)) if (v !== "") num[k] = k === "leadQuality" ? v : Number(v);
      if (m.leadQuality) num.leadQuality = m.leadQuality;
      const r = await api.post<any>("/scaling/analyze", { brandId, metrics: num });
      if (r.error === "ai_unparsable") { setErr("ลองใหม่"); return; } setOut(r.output); setIsMock(r.isMock);
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); } finally { setLoading(false); }
  }

  const cls = out ? CLASSIFICATIONS[out.classification] : null;

  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))" }}>
          {M_FIELDS.map((f) => (
            <div className="mh-field" style={{ margin: 0 }} key={f.key}><label className="mh-label" style={{ fontSize: 12.5 }}>{f.label}</label><input className="mh-input" type="number" value={m[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} /></div>
          ))}
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label" style={{ fontSize: 12.5 }}>คุณภาพ Lead</label><select className="mh-select" value={m.leadQuality ?? ""} onChange={(e) => set("leadQuality", e.target.value)}><option value="">—</option><option value="ดี">ดี</option><option value="กลาง">กลาง</option><option value="แย่">แย่</option></select></div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={analyze} disabled={loading}>{loading ? <span className="spinner" /> : <><Icon name="TrendingUp" size={18} /> วิเคราะห์การ Scale</>}</button>
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /></div>}
      {out && cls && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>วิเคราะห์จาก <b>Mock</b> — ใส่ Claude Key เพื่อการตัดสินใจเชิงลึกจริง</div></div>}
          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <div className="mh-spread">
              <div><div className="mh-muted" style={{ fontSize: 12.5 }}>จัดประเภท</div><span className={"mh-pill " + cls.pill} style={{ fontSize: 15, padding: "5px 14px" }}>{cls.label}</span></div>
              <div style={{ textAlign: "right" }}><div className="mh-muted" style={{ fontSize: 12.5 }}>คำแนะนำ</div><b style={{ fontSize: 17, color: "var(--blue-600)" }}>{out.recommendationLabel}</b></div>
            </div>
            <div className="mh-sec" style={{ marginTop: 10 }}>{out.classificationReason}</div>
          </div>
          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, marginBottom: 10 }}>วิเคราะห์รายด้าน</h3>
            {out.analysis.map((a, i) => <div key={i} className="mh-row" style={{ gap: 10, padding: "6px 0", borderBottom: "1px solid var(--border)" }}><b style={{ minWidth: 130, color: TONE[a.tone] }}>{a.aspect}</b><span style={{ fontSize: 13.8 }}>{a.reading}</span></div>)}
          </div>
          <div className="form-grid" style={{ alignItems: "start" }}>
            <div className="mh-card card-pad"><h3 style={{ fontSize: 14, marginBottom: 8 }}>📋 แผนดำเนินการ</h3><ul style={{ margin: 0, paddingLeft: 18 }}>{out.plan.map((p, i) => <li key={i} style={{ marginBottom: 5 }}>{p}</li>)}</ul></div>
            <div className="mh-card card-pad" style={{ background: "var(--amber-050)", borderColor: "#f2dca0" }}><h3 style={{ fontSize: 14, marginBottom: 8 }}>⚠️ ความเสี่ยง</h3><div style={{ fontSize: 14 }}>{out.risk}</div></div>
          </div>
          <div className="mh-card card-pad" style={{ marginTop: 14 }}>
            <div style={{ marginBottom: 6 }}><span className="mh-muted" style={{ fontSize: 12.5 }}>เหตุผล: </span>{out.reason}</div>
            <div style={{ marginBottom: 6 }}><span className="mh-muted" style={{ fontSize: 12.5 }}>👀 เฝ้าดู: </span>{out.whatToWatch}</div>
            <div><span className="mh-muted" style={{ fontSize: 12.5 }}>🕐 รีวิวเมื่อ: </span>{out.reviewPoint}</div>
          </div>
        </>
      )}
    </>
  );
}

const CMP_FIELDS = ["spend", "lead", "cpl", "ctr", "cpc", "cpm", "frequency", "sale", "revenue", "roas"];
const CMP_LABEL: Record<string, string> = { spend: "Spend", lead: "Lead", cpl: "CPL", ctr: "CTR", cpc: "CPC", cpm: "CPM", frequency: "Freq", sale: "Sale", revenue: "Revenue", roas: "ROAS" };

function CompareTab() {
  const [cur, setCur] = useState<Record<string, string>>({});
  const [prev, setPrev] = useState<Record<string, string>>({});
  const [out, setOut] = useState<ComparisonOutput | null>(null);
  const [loading, setLoading] = useState(false);

  async function compare() {
    setLoading(true);
    try {
      const n = (o: Record<string, string>) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)]));
      const r = await api.post<{ output: ComparisonOutput }>("/scaling/compare", { current: n(cur), previous: n(prev) });
      setOut(r.output);
    } finally { setLoading(false); }
  }

  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead><tr><th style={{ textAlign: "left", fontSize: 12.5, color: "var(--text-muted)", padding: 4 }}>เมทริกซ์</th>{CMP_FIELDS.map((f) => <th key={f} style={{ fontSize: 12.5, color: "var(--text-muted)", padding: 4 }}>{CMP_LABEL[f]}</th>)}</tr></thead>
            <tbody>
              <tr><td style={{ fontSize: 13, fontWeight: 600, padding: 4 }}>ช่วงนี้</td>{CMP_FIELDS.map((f) => <td key={f} style={{ padding: 2 }}><input className="mh-input" style={{ padding: "6px 8px", fontSize: 13 }} type="number" value={cur[f] ?? ""} onChange={(e) => setCur((p) => ({ ...p, [f]: e.target.value }))} /></td>)}</tr>
              <tr><td style={{ fontSize: 13, fontWeight: 600, padding: 4 }}>ช่วงก่อน</td>{CMP_FIELDS.map((f) => <td key={f} style={{ padding: 2 }}><input className="mh-input" style={{ padding: "6px 8px", fontSize: 13 }} type="number" value={prev[f] ?? ""} onChange={(e) => setPrev((p) => ({ ...p, [f]: e.target.value }))} /></td>)}</tr>
            </tbody>
          </table>
        </div>
        <button className="mh-btn mh-btn--primary mh-btn--lg" style={{ marginTop: 14 }} onClick={compare} disabled={loading}>{loading ? <span className="spinner" /> : <><Icon name="TrendingUp" size={18} /> เทียบผล</>}</button>
      </div>
      {out && (
        <>
          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))" }}>
              {out.rows.filter((r) => r.changePct != null).map((r, i) => (
                <div key={i} style={{ background: "var(--gray-050)", borderRadius: 8, padding: 10, textAlign: "center" }}>
                  <div className="mh-muted" style={{ fontSize: 12 }}>{r.metric}</div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: TONE[r.tone] }}>{r.changePct! > 0 ? "+" : ""}{r.changePct}%</div>
                  <div className="mh-muted" style={{ fontSize: 11 }}>{r.previous} → {r.current}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mh-card card-pad" style={{ background: "var(--blue-050)", borderColor: "var(--blue-100)" }}><b>AI สรุป:</b> <span className="mh-sec">{out.explanation}</span></div>
        </>
      )}
    </>
  );
}
